import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, ExternalLink } from 'lucide-react';

export const PressRelease = () => {
  return (
    <div className="pt-20" data-testid="press-release-page">
      {/* Header */}
      <section className="py-12 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link 
            to="/grants" 
            className="inline-flex items-center text-[#5B4FF9] hover:text-[#4A3FE8] font-medium mb-6 transition-colors"
            data-testid="back-to-grants"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Grants
          </Link>
          
          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
            <span className="bg-[#5B4FF9] text-white px-3 py-1 rounded-full text-xs font-semibold">
              Press Release
            </span>
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              May 21, 2026
            </span>
            <span className="flex items-center">
              <MapPin className="h-4 w-4 mr-1" />
              Minneapolis, MN
            </span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-[#272926] leading-tight">
            Players Health Foundation Launches $100,000 Grant Program to Protect Young Athletes
          </h1>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Lead */}
          <p className="text-xl text-[#5B4FF9] font-semibold mb-8 leading-relaxed">
            Twenty grants of $5,000 each will fund youth sports organizations committed to athlete safety, training support, and community impact.
          </p>

          {/* Body */}
          <div className="prose prose-lg max-w-none text-zinc-700 space-y-6">
            <p>
              <strong>MINNEAPOLIS — May 21, 2026</strong> — The Players Health Foundation today announced the launch of its 2026 grant program, committing $100,000 to youth sports organizations across the country. The program will award 20 grants of $5,000 each to organizations dedicated to protecting and advancing the wellbeing of young athletes — from on-field safety infrastructure to training and development resources.
            </p>

            <p>
              The initiative reflects the Foundation's core mission to be a catalyst to protect athletes and open doors to the power of sport for all. Rather than funding individual athletes, grants will be directed to organizations where the investment can create measurable, scalable impact across entire communities of youth sports participants. Grant funds may be used to support a range of athlete safety and training priorities, including equipment, coaching education, safety protocol development, injury prevention programs, and other initiatives that directly benefit young athletes in the organization's care.
            </p>

            {/* Quote 1 */}
            <blockquote className="border-l-4 border-[#5B4FF9] pl-6 py-4 my-8 bg-[#F5F4FF] rounded-r-xl">
              <p className="text-lg italic text-[#272926] mb-4">
                "We want to ensure every young athlete gets the chance to step onto the playing field and does so with proper safety resources. The Players Health Foundation exists because both of these issues — safety and accessibility — are urgent, and both are solvable. This grant program is a direct investment in the organizations doing that work on the ground, right now."
              </p>
              <footer className="text-sm font-semibold text-[#5B4FF9]">
                — Benita Fitzgerald Mosley, Players Health Foundation Board Chair
              </footer>
            </blockquote>

            <p>
              Applications are open to nonprofit and community-based sports organizations of all sizes. The Foundation is particularly focused on reaching organizations where a $5,000 grant can make a meaningful, measurable difference — prioritizing those with demonstrated commitment to athlete welfare and a clear plan for how funding will be deployed.
            </p>

            <p>
              The grant program is open to registered 501(c)(3) nonprofits and fiscally sponsored organizations that have been delivering organized youth sports programming for a minimum of three years. Eligible organizations must serve athletes between the ages of 6 and 18, with at least 50 percent of participants coming from low-income families, as demonstrated through Free and Reduced Price Lunch qualification or enrollment in Title I schools.
            </p>

            <p>
              In addition to meeting these criteria, grant recipients will be expected to participate in Players Health Foundation-provided safety training, maintain the capacity to track and report program, community, and family impact, and be willing to share stories of participant impact as part of the Foundation's broader storytelling efforts.
            </p>

            {/* Quote 2 */}
            <blockquote className="border-l-4 border-amber-400 pl-6 py-4 my-8 bg-amber-50 rounded-r-xl">
              <p className="text-lg italic text-[#272926] mb-4">
                "Sport gave me something as a kid that I never took for granted — a place to belong, to feel safe, to grow. That's why safety and access have always been at the heart of everything we do at Players Health. The Foundation is a natural extension of that mission. These grants go to the organizations showing up every day to protect kids and keep the doors of sports participation open for everyone. I know what that work means — and I know how much it matters."
              </p>
              <footer className="text-sm font-semibold text-amber-600">
                — Tyrre Burks, Players Health Founder and Foundation Board President
              </footer>
            </blockquote>

            <p>
              Applications are submitted online through the Players Health Foundation website via Submittable. The grant portal is open as of today, May 21, 2026, and the deadline for applications is June 15. Grant recipients will be announced later this summer.
            </p>

            {/* CTA */}
            <div className="bg-[#5B4FF9] rounded-2xl p-8 my-10 text-center">
              <h3 className="text-xl font-bold text-white mb-4">Ready to Apply?</h3>
              <p className="text-white/90 mb-6">Visit the Players Health Foundation website to learn more about eligibility criteria, program details, and to access the application.</p>
              <a
                href="https://playershealthfoundation.submittable.com/submit"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#5B4FF9] rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
                data-testid="press-release-apply-btn"
              >
                Start Application
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </div>

            {/* About Sections */}
            <div className="border-t border-zinc-200 pt-10 mt-10">
              <h3 className="text-lg font-bold text-[#272926] mb-4">About Players Health Foundation</h3>
              <p className="text-zinc-600 mb-8">
                The Players Health Foundation is a catalyst for protecting athletes and opening doors to the power of sport for all. Sport has the power to change lives — but only when it is safe and accessible. The Foundation works to address both challenges together: taking meaningful action to create safer, more positive outcomes in youth sports while uniting organizations, partners, and communities across the sports ecosystem to expand access and opportunity. Through collaboration, accountability, and a commitment to lasting impact, the Foundation inspires generosity and builds the systems needed to ensure every young athlete has the chance to play.
              </p>

              <h3 className="text-lg font-bold text-[#272926] mb-4">About Players Health</h3>
              <p className="text-zinc-600 mb-8">
                Players Health provides insurance protection and risk-management solutions for youth, amateur, fitness, and collegiate sports nationwide. Protecting more than 5.5 million athletes across 40,000 programs, Players Health delivers insurance coverage, athlete safety education and compliance resources that help create safer environments and reduce risk. Guided by the belief that every athlete deserves a safe place to play, Players Health works to strengthen safety standards across the sports ecosystem. Learn more at{' '}
                <a href="https://www.playershealth.com" target="_blank" rel="noopener noreferrer" className="text-[#5B4FF9] hover:underline">
                  www.playershealth.com
                </a>.
              </p>

              {/* Media Contact */}
              <div className="bg-zinc-100 rounded-xl p-6">
                <h4 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-3">Media Contact</h4>
                <p className="text-[#272926] font-semibold">Kyle Lubrano Satola</p>
                <a href="mailto:kyle.lubrano@playershealth.com" className="text-[#5B4FF9] hover:underline">
                  kyle.lubrano@playershealth.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
